<script setup lang="ts">
import { watchEffect, ref, nextTick } from 'vue'
import {
    FilterType,
    type CheckboxFilter as CheckboxFilterType,
    type FilterSection,
    type RadioFilter as RadioFilterType,
    type RangeFilter as RangeFilterType
} from '../../../interfaces/filters'
import RangeFilter from './RangeFilter.vue'
import CheckboxFilter from './CheckboxFilter.vue'
import RadioFilter from './RadioFilter.vue'

const section = defineModel<FilterSection>({
    required: true
})

defineProps({
    readOnly: {
        type: Boolean,
        default: false
    }
})

const showAllFilters = ref(false)
const showAllFiltersButton = ref<HTMLElement | null>(null)

// we need to use this method because v-model doesn't work with arrays objects references
const updateFilter = (
    updatedFilter: RangeFilterType | CheckboxFilterType | RadioFilterType
) => {
    if (updatedFilter.type !== FilterType.RADIO) {
        const index = section.value.filters.findIndex(
            (filter) => filter.name === updatedFilter.name
        )

        if (index !== -1) {
            section.value.filters[index] = updatedFilter
        }
    }
}

const updateRadioFilter = (value: string) => {
    section.value.filters.forEach((filter) => {
        if (filter.type === FilterType.RADIO) {
            filter.checked = filter.value === value
        }
    })
}

watchEffect(() => {
    if (showAllFilters.value) {
        nextTick(() => {
            showAllFiltersButton.value?.scrollIntoView({ behavior: 'smooth' })
        })
    }
})
</script>

<template>
    <div class="flex flex-col gap-6">
        <span v-if="section.title" class="font-semibold">
            {{ section.title }}
        </span>
        <div
            v-for="(filter, index) in section.filters"
            v-show="index < 6 || showAllFilters || !section.showMore"
            :key="filter.name"
        >
            <RangeFilter
                v-if="filter.type === FilterType.RANGE && !filter.invisible"
                :model-value="filter"
                :read-only
                @update:modelValue="updateFilter($event)"
            />
            <CheckboxFilter
                v-else-if="
                    filter.type === FilterType.CHECKBOX && !filter.invisible
                "
                :model-value="filter"
                :read-only
                @update:modelValue="updateFilter($event)"
            />
            <RadioFilter
                v-else-if="
                    filter.type === FilterType.RADIO && !filter.invisible
                "
                :filter
                :read-only
                @input="updateRadioFilter"
            />
        </div>

        <button
            v-if="section.filters.length > 6 && section.showMore"
            ref="showAllFiltersButton"
            type="button"
            class="text-left flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm transition-colors ease-in-out duration-300"
            @click="showAllFilters = !showAllFilters"
        >
            <IconChevronDown
                class="size-4 transform transition-transform duration-300"
                :class="{ 'rotate-180': showAllFilters }"
            />

            {{
                showAllFilters ? $t('filters.see_less') : $t('filters.see_more')
            }}
        </button>
    </div>
</template>
